import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Confetti from "react-confetti";
import { PartyPopper } from "lucide-react";
import { ASSETS, JOIN_PTA_URL } from "./questions";

const useWindowSize = () => {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
};

export const SuccessScreen = () => {
  const { width, height } = useWindowSize();
  return (
    <div data-testid="success-screen" className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">
      <Confetti
        width={width}
        height={height}
        numberOfPieces={280}
        recycle={true}
        colors={["#EC4899", "#06B6D4", "#EAB308", "#0F172A", "#FFFFFF"]}
        style={{ position: "fixed", top: 0, left: 0, zIndex: 40, pointerEvents: "none" }}
      />

      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
        className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-yellow-400 bg-white shadow-[0_0_28px_rgba(234,179,8,0.5)]"
      >
        <PartyPopper size={38} className="text-pink-500" />
      </motion.div>

      <motion.h2
        data-testid="success-title"
        className="font-display mt-6 text-4xl sm:text-5xl font-black uppercase tracking-tight"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 160, damping: 15 }}
      >
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-yellow-400">
          Success!
        </span>
      </motion.h2>

      <motion.p
        data-testid="success-message"
        className="mt-4 max-w-sm text-base sm:text-lg leading-relaxed text-slate-700"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        You just scored <span className="font-bold text-pink-600">50 extra entries</span> to win the giant
        Ashley Falls Eagle Plushie! Want even <span className="font-bold text-cyan-600">MORE chances to win</span>?
        Check out the options below!
      </motion.p>

      <motion.img
        src={ASSETS.plushie}
        alt="Giant Ashley Falls Eagle Plushie"
        className="mt-6 h-44 w-auto drop-shadow-[0_14px_24px_rgba(15,23,42,0.18)]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: [0, -8, 0] }}
        transition={{ delay: 0.55, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 } }}
      />

      <motion.div
        className="mt-8 w-full flex flex-col items-center"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <p data-testid="pta-entries-text" className="font-display text-sm font-black uppercase tracking-[0.12em] text-slate-700">
          Earn up to <span className="text-pink-600">250 extra entries!</span>
        </p>
        <a
          data-testid="join-pta-link"
          href={JOIN_PTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="neon-pulse mt-3 block w-full rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 px-6 py-5 font-display text-lg font-black uppercase tracking-wide text-white transition-transform active:scale-[0.97]"
        >
          Join the PTA
        </a>
        <a
          data-testid="already-member-link"
          href={JOIN_PTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 text-sm font-bold text-slate-400 underline underline-offset-4 decoration-slate-300 hover:text-cyan-600 hover:decoration-cyan-400 transition-colors"
        >
          Already a PTA member?
        </a>

        <div className="my-6 h-px w-2/3 bg-gradient-to-r from-transparent via-slate-200 to-transparent" aria-hidden="true" />

        <p data-testid="steam-entries-text" className="font-display text-sm font-black uppercase tracking-[0.12em] text-slate-700">
          Earn an extra <span className="text-cyan-600">100 entries!</span>
        </p>
        <a
          data-testid="donate-steam-link"
          href="https://app.etapestry.com/hosted/DelMarSchoolsEducationFoun/OnlineDonation.html"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 block w-full rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 px-6 py-5 font-display text-lg font-black uppercase tracking-wide text-white shadow-[0_0_18px_rgba(6,182,212,0.4)] transition-transform active:scale-[0.97]"
        >
          Donate to STEAM+
        </a>
      </motion.div>
    </div>
  );
};
